// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * Fixed-row-height virtual list - renders only the rows a viewport can actually show, plus a small
 * overscan buffer, regardless of how many items the caller hands it. The DOM never holds more than
 * 100 list rows at any time - the whole reason this class exists rather than each browser component
 * rendering its own <table> body directly, the way the PHP-rendered rule browser of
 * class-svtd-renderer.php did.
 *
 * ⚠️ FIXED ROW HEIGHT, ON PURPOSE - variable-height virtualisation needs to measure rendered rows
 * before it can compute scroll offsets, which reintroduces exactly the "render everything once to
 * find out how tall it is" cost this class exists to avoid. Every row in both browsers is one line
 * of text in a fixed-height table-like row, so a fixed height is not a simplification that loses
 * anything real.
 *
 * Usage: `new VirtualList({ container, rowHeight, overscan, renderRow })`, then `list.setItems(arr)`
 * whenever the (already filtered, in-memory) item array changes. The container must have a fixed or
 * flex-derived height and `overflow-y: auto` - this class does not manage the container's own size.
 */

export class VirtualList {

	/**
	 * @param {{
	 *   container: HTMLElement,
	 *   rowHeight: number,
	 *   overscan?: number,
	 *   renderRow: (item:*, index:number) => HTMLElement,
	 * }} opts
	 */
	constructor( opts ) {

		this._container = opts.container;
		this._rowHeight  = opts.rowHeight;
		this._overscan   = opts.overscan ?? 8;
		this._renderRow  = opts.renderRow;

		this._items = [];

		// two children only: a "sizer" whose height is items.length * rowHeight (gives the container
		// its real scrollbar), and a "viewport" absolutely positioned inside it, holding only the
		// currently-visible rows, translated to the right scroll offset.
		this._sizer = document.createElement( 'div' );
		this._sizer.style.position = 'relative';

		this._viewport = document.createElement( 'div' );
		this._viewport.style.position = 'absolute';
		this._viewport.style.left = '0';
		this._viewport.style.right = '0';
		this._viewport.style.top = '0';

		this._sizer.appendChild( this._viewport );
		this._container.appendChild( this._sizer );

		this._rowPool = new Map(); // index -> element, reused across scroll frames

		this._pendingFrame = null;

		this._container.addEventListener( 'scroll', () => this._scheduleRender(), { passive: true } );

		if ( typeof ResizeObserver !== 'undefined' ) {
			this._resizeObserver = new ResizeObserver( () => this._scheduleRender() );
			this._resizeObserver.observe( this._container );
		}

	}

	/** @param {Array<*>} items */
	setItems( items ) {

		// ⚠️ DEFENSIVE, NOT DECORATIVE - measured in a browser check: a caller that clears its own
		// container's innerHTML AFTER constructing this class (e.g. a "loading…" reset) silently
		// detaches the sizer this constructor already appended, and every row from then on renders into
		// a viewport that is no longer part of the visible DOM - no error, no empty-state message, just
		// nothing on screen. Re-attach rather than trust the caller never does this.
		if ( this._sizer.parentElement !== this._container )
			this._container.appendChild( this._sizer );

		this._items = items;
		this._sizer.style.height = ( items.length * this._rowHeight ) + 'px';
		this._container.scrollTop = 0;
		this._rowPool.clear();
		this._viewport.innerHTML = '';
		this._render();

	}

	/** Number of rows currently attached to the DOM - exposed for tests only. */
	get domRowCount() {
		return this._viewport.childElementCount;
	}

	destroy() {
		if ( this._resizeObserver ) this._resizeObserver.disconnect();
	}

	_scheduleRender() {

		if ( this._pendingFrame ) return;

		this._pendingFrame = requestAnimationFrame( () => {
			this._pendingFrame = null;
			this._render();
		} );

	}

	_render() {

		const total = this._items.length;

		if ( total === 0 ) {
			this._viewport.innerHTML = '';
			this._rowPool.clear();
			return;
		}

		const viewportHeight = this._container.clientHeight || 400;
		const scrollTop      = this._container.scrollTop;

		const firstVisible = Math.floor( scrollTop / this._rowHeight );
		const visibleCount  = Math.ceil( viewportHeight / this._rowHeight );

		const start = Math.max( 0, firstVisible - this._overscan );
		const end   = Math.min( total, firstVisible + visibleCount + this._overscan );

		// drop rows that scrolled out of the new [start,end) window
		for ( const [ index, el ] of this._rowPool )
			if ( index < start || index >= end ) {
				el.remove();
				this._rowPool.delete( index );
			}

		for ( let i = start; i < end; i++ ) {

			if ( this._rowPool.has( i ) )
				continue;

			const el = this._renderRow( this._items[ i ], i );
			el.style.position = 'absolute';
			el.style.left = '0';
			el.style.right = '0';
			el.style.top = ( i * this._rowHeight ) + 'px';
			el.style.height = this._rowHeight + 'px';

			this._viewport.appendChild( el );
			this._rowPool.set( i, el );

		}

	}

}
